/**
 * @module language
 * Language translation functionality using Google Translate API
 * Falls back to styled static dropdown when API key is not available
 */
(function () {
    'use strict';

    // Configuration - set your Google Translate API key here
    const CONFIG = {
        apiKey: null, // Set this to your Google Translate API key
        useWidget: false, // Use API method by default to preserve styling
        defaultLanguage: 'en',
        maxRetries: 3,
        batchSize: 100, // Maximum texts to translate in one API call
        cacheExpiry: 24 * 60 * 60 * 1000 // 24 hours in milliseconds
    };

    // State management
    let isTranslating = false;
    let currentLanguage = CONFIG.defaultLanguage;
    let translationCache = new Map();
    let supportedLanguages = [];

    // Default fallback languages if API fails
    const DEFAULT_LANGUAGES = [
        { code: 'af', name: 'Afrikaans' },
        { code: 'sq', name: 'Albanian' },
        { code: 'am', name: 'Amharic' },
        { code: 'ar', name: 'Arabic' },
        { code: 'hy', name: 'Armenian' },
        { code: 'az', name: 'Azerbaijani' },
        { code: 'eu', name: 'Basque' },
        { code: 'be', name: 'Belarusian' },
        { code: 'bn', name: 'Bengali' },
        { code: 'bs', name: 'Bosnian' },
        { code: 'bg', name: 'Bulgarian' },
        { code: 'ca', name: 'Catalan' },
        { code: 'ceb', name: 'Cebuano' },
        { code: 'zh-CN', name: 'Chinese (Simplified)' },
        { code: 'zh-TW', name: 'Chinese (Traditional)' },
        { code: 'co', name: 'Corsican' },
        { code: 'hr', name: 'Croatian' },
        { code: 'cs', name: 'Czech' },
        { code: 'da', name: 'Danish' },
        { code: 'nl', name: 'Dutch' },
        { code: 'en', name: 'English' },
        { code: 'eo', name: 'Esperanto' },
        { code: 'et', name: 'Estonian' },
        { code: 'fi', name: 'Finnish' },
        { code: 'fr', name: 'French' },
        { code: 'fy', name: 'Frisian' },
        { code: 'gl', name: 'Galician' },
        { code: 'ka', name: 'Georgian' },
        { code: 'de', name: 'German' },
        { code: 'el', name: 'Greek' },
        { code: 'gu', name: 'Gujarati' },
        { code: 'ht', name: 'Haitian Creole' },
        { code: 'ha', name: 'Hausa' },
        { code: 'haw', name: 'Hawaiian' },
        { code: 'he', name: 'Hebrew' },
        { code: 'hi', name: 'Hindi' },
        { code: 'hmn', name: 'Hmong' },
        { code: 'hu', name: 'Hungarian' },
        { code: 'is', name: 'Icelandic' },
        { code: 'ig', name: 'Igbo' },
        { code: 'id', name: 'Indonesian' },
        { code: 'ga', name: 'Irish' },
        { code: 'it', name: 'Italian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'jw', name: 'Javanese' },
        { code: 'kn', name: 'Kannada' },
        { code: 'kk', name: 'Kazakh' },
        { code: 'km', name: 'Khmer' },
        { code: 'ko', name: 'Korean' },
        { code: 'ku', name: 'Kurdish' },
        { code: 'ky', name: 'Kyrgyz' },
        { code: 'lo', name: 'Lao' },
        { code: 'la', name: 'Latin' },
        { code: 'lv', name: 'Latvian' },
        { code: 'lt', name: 'Lithuanian' },
        { code: 'lb', name: 'Luxembourgish' },
        { code: 'mk', name: 'Macedonian' },
        { code: 'mg', name: 'Malagasy' },
        { code: 'ms', name: 'Malay' },
        { code: 'ml', name: 'Malayalam' },
        { code: 'mt', name: 'Maltese' },
        { code: 'mi', name: 'Maori' },
        { code: 'mr', name: 'Marathi' },
        { code: 'mn', name: 'Mongolian' },
        { code: 'my', name: 'Myanmar (Burmese)' },
        { code: 'ne', name: 'Nepali' },
        { code: 'no', name: 'Norwegian' },
        { code: 'ps', name: 'Pashto' },
        { code: 'fa', name: 'Persian' },
        { code: 'pl', name: 'Polish' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'pa', name: 'Punjabi' },
        { code: 'ro', name: 'Romanian' },
        { code: 'ru', name: 'Russian' },
        { code: 'sm', name: 'Samoan' },
        { code: 'gd', name: 'Scots Gaelic' },
        { code: 'sr', name: 'Serbian' },
        { code: 'st', name: 'Sesotho' },
        { code: 'sn', name: 'Shona' },
        { code: 'sd', name: 'Sindhi' },
        { code: 'si', name: 'Sinhala' },
        { code: 'sk', name: 'Slovak' },
        { code: 'sl', name: 'Slovenian' },
        { code: 'so', name: 'Somali' },
        { code: 'es', name: 'Spanish' },
        { code: 'su', name: 'Sundanese' },
        { code: 'sw', name: 'Swahili' },
        { code: 'sv', name: 'Swedish' },
        { code: 'tl', name: 'Tagalog' },
        { code: 'tg', name: 'Tajik' },
        { code: 'ta', name: 'Tamil' },
        { code: 'te', name: 'Telugu' },
        { code: 'th', name: 'Thai' },
        { code: 'tr', name: 'Turkish' },
        { code: 'uk', name: 'Ukrainian' },
        { code: 'ur', name: 'Urdu' },
        { code: 'uz', name: 'Uzbek' },
        { code: 'vi', name: 'Vietnamese' },
        { code: 'cy', name: 'Welsh' },
        { code: 'xh', name: 'Xhosa' },
        { code: 'yi', name: 'Yiddish' },
        { code: 'yo', name: 'Yoruba' },
        { code: 'zu', name: 'Zulu' }
    ];

    // Initialize when DOM is ready
    document.addEventListener('DOMContentLoaded', function() {
        init();
    });

    /**
     * Initialize the language translation system
     */
    function init() {
        console.log('Initializing language translation system...');
        
        // Load saved language preference
        loadSavedLanguage();
        
        // Initialize translation method
        if (CONFIG.useWidget) {
            initGoogleTranslateWidget();
        } else {
            if (CONFIG.apiKey) {
                initApiTranslation();
            } else {
                console.warn('Google Translate API key not configured. Using static dropdown with fallback languages.');
                initStaticTranslation();
            }
        }
    }

    /**
     * Initialize Google Translate API method
     */
    async function initApiTranslation() {
        try {
            // Load supported languages from API
            await loadSupportedLanguages();
            
            // Populate the language selector
            populateLanguageSelector();
            
            // Initialize event listeners
            initLanguageSelector();
            
            console.log('Google Translate API initialized successfully');
        } catch (error) {
            console.error('Failed to initialize API translation:', error);
            console.log('Falling back to static translation...');
            initStaticTranslation();
        }
    }

    /**
     * Initialize static translation (preserves styling, no API needed)
     */
    function initStaticTranslation() {
        console.log('Initializing static translation with fallback languages...');
        
        // Use default languages
        supportedLanguages = DEFAULT_LANGUAGES;
        
        // Populate the language selector
        populateLanguageSelector();
        
        // Initialize event listeners for static mode
        initStaticLanguageSelector();
        
        console.log('Static translation initialized successfully');
    }

    /**
     * Initialize Google Translate Widget
     */
    function initGoogleTranslateWidget() {
        console.log('Initializing Google Translate Widget...');
        
        // Check if widget is already loaded
        if (window.google && window.google.translate) {
            setupGoogleTranslateWidget();
            return;
        }
        
        // Create callback function
        window.googleTranslateElementInit = setupGoogleTranslateWidget;
        
        // Load Google Translate script
        const script = document.createElement('script');
        script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.onerror = function() {
            console.error('Failed to load Google Translate Widget');
            // Fall back to static translation
            initStaticTranslation();
        };
        document.head.appendChild(script);
    }

    /**
     * Setup Google Translate Widget
     */
    function setupGoogleTranslateWidget() {
        const selector = document.getElementById('language-toggle');
        if (!selector) return;
        
        // Hide the original select
        selector.style.display = 'none';
        
        // Create container for Google Translate widget
        const container = selector.parentNode;
        const translateDiv = document.createElement('div');
        translateDiv.id = 'google_translate_element';
        translateDiv.className = 'qld__header__language-toggle-widget';
        
        container.appendChild(translateDiv);
        
        // Initialize Google Translate Element
        new google.translate.TranslateElement({
            pageLanguage: CONFIG.defaultLanguage,
            includedLanguages: 'ar,zh-CN,zh-TW,fr,de,hi,it,ja,ko,pt,ru,es,th,vi',
            layout: google.translate.TranslateElement.InlineLayout.SIMPLE,
            autoDisplay: false,
            gaTrack: true,
            gaId: 'UA-XXXXX-X' // Replace with your Google Analytics ID if needed
        }, 'google_translate_element');
        
        console.log('Google Translate Widget initialized successfully');
    }

    /**
     * Load supported languages from Google Translate API
     */
    async function loadSupportedLanguages() {
        if (!CONFIG.apiKey) {
            throw new Error('API key not configured');
        }

        try {
            const response = await fetch(
                `https://translation.googleapis.com/language/translate/v2/languages?key=${CONFIG.apiKey}&target=en`,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    }
                }
            );

            if (!response.ok) {
                throw new Error(`API request failed: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            
            if (data.error) {
                throw new Error(`API error: ${data.error.message}`);
            }

            supportedLanguages = data.data.languages.map(lang => ({
                code: lang.language,
                name: lang.name
            }));

            console.log(`Loaded ${supportedLanguages.length} supported languages from API`);
            return supportedLanguages;
        } catch (error) {
            console.warn('Failed to load languages from API, using default list:', error);
            supportedLanguages = DEFAULT_LANGUAGES;
            return supportedLanguages;
        }
    }

    /**
     * Populate the language selector dropdown(s)
     */
    function populateLanguageSelector(languages = supportedLanguages) {
        const selectors = [
            document.getElementById('language-toggle'),
            document.getElementById('language-toggle-mega')
        ].filter(Boolean); // Remove null elements

        if (selectors.length === 0) return;

        selectors.forEach(selector => {
            // Clear existing options except the first (placeholder)
            while (selector.children.length > 1) {
                selector.removeChild(selector.lastChild);
            }

            // Add supported languages
            languages.forEach(lang => {
                const option = document.createElement('option');
                option.value = lang.code;
                option.textContent = lang.name;
                
                // Mark current language as selected
                if (lang.code === currentLanguage) {
                    option.selected = true;
                }
                
                selector.appendChild(option);
            });
        });

        console.log(`Populated ${selectors.length} language selector(s) with ${languages.length} languages`);
    }

    /**
     * Initialize language selector event listeners
     */
    function initLanguageSelector() {
        const selectors = [
            document.getElementById('language-toggle'),
            document.getElementById('language-toggle-mega')
        ].filter(Boolean); // Remove null elements

        if (selectors.length === 0) return;

        selectors.forEach(selector => {
            selector.addEventListener('change', function(e) {
                const selectedLanguage = e.target.value;
                
                if (!selectedLanguage) {
                    // Reset to original language
                    resetTranslation();
                    return;
                }

                if (selectedLanguage === currentLanguage) {
                    return; // No change needed
                }

                // Sync both selectors
                syncLanguageSelectors(selectedLanguage);
                
                translatePage(selectedLanguage);
            });
        });

        console.log(`Language selector event listeners initialized for ${selectors.length} selector(s)`);
    }

    /**
     * Initialize language selector for static mode (no translation, just UI)
     */
    function initStaticLanguageSelector() {
        const selectors = [
            document.getElementById('language-toggle'),
            document.getElementById('language-toggle-mega')
        ].filter(Boolean); // Remove null elements

        if (selectors.length === 0) return;

        selectors.forEach(selector => {
            selector.addEventListener('change', function(e) {
                const selectedLanguage = e.target.value;
                
                if (!selectedLanguage) {
                    return;
                }

                // Sync both selectors
                syncLanguageSelectors(selectedLanguage);
                
                // Show informational message instead of translating
                showStaticLanguageInfo(selectedLanguage);
            });
        });

        console.log(`Static language selector event listeners initialized for ${selectors.length} selector(s)`);
    }

    /**
     * Show information about selected language in static mode
     */
    function showStaticLanguageInfo(languageCode) {
        const languageName = supportedLanguages.find(lang => lang.code === languageCode)?.name || languageCode;
        
        console.log(`Language selected: ${languageName} (${languageCode})`);
        
        // You can customize this message
        const message = `Translation to ${languageName} is available when API key is configured.`;
        
        // Show a subtle notification
        showTranslationInfo(message);
    }

    /**
     * Show translation info message
     */
    function showTranslationInfo(message) {
        // Remove any existing info message
        const existingInfo = document.getElementById('translation-info');
        if (existingInfo) {
            existingInfo.remove();
        }

        const infoMessage = document.createElement('div');
        infoMessage.id = 'translation-info';
        infoMessage.textContent = message;
        infoMessage.style.cssText = `
            position: fixed;
            top: 20px;
            right: 20px;
            background: #f0f9ff;
            border: 1px solid #0284c7;
            color: #0c4a6e;
            padding: 12px 16px;
            border-radius: 6px;
            font-size: 14px;
            z-index: 9999;
            max-width: 300px;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
        `;
        
        document.body.appendChild(infoMessage);
        
        // Remove message after 4 seconds
        setTimeout(() => {
            infoMessage.remove();
        }, 4000);
    }

    /**
     * Synchronize both language selectors
     */
    function syncLanguageSelectors(selectedLanguage) {
        const selectors = [
            document.getElementById('language-toggle'),
            document.getElementById('language-toggle-mega')
        ].filter(Boolean);

        selectors.forEach(selector => {
            if (selector.value !== selectedLanguage) {
                selector.value = selectedLanguage;
            }
        });
    }

    /**
     * Translate the entire page to the target language
     */
    async function translatePage(targetLanguage) {
        if (isTranslating) {
            console.log('Translation already in progress');
            return;
        }

        if (targetLanguage === CONFIG.defaultLanguage) {
            resetTranslation();
            return;
        }

        isTranslating = true;
        showTranslationLoading();

        try {
            // Get all translatable elements
            const textElements = getTranslatableElements();
            
            // Extract unique texts to translate
            const textsToTranslate = extractTextsForTranslation(textElements);
            
            if (textsToTranslate.length === 0) {
                throw new Error('No translatable content found');
            }

            // Check cache first
            const cachedTranslations = getCachedTranslations(textsToTranslate, targetLanguage);
            const uncachedTexts = textsToTranslate.filter(text => !cachedTranslations.has(text));

            let allTranslations = new Map(cachedTranslations);

            // Translate uncached texts
            if (uncachedTexts.length > 0) {
                console.log(`Translating ${uncachedTexts.length} new texts to ${targetLanguage}`);
                const newTranslations = await translateTexts(uncachedTexts, targetLanguage);
                
                // Cache new translations
                cacheTranslations(uncachedTexts, newTranslations, targetLanguage);
                
                // Merge with cached translations
                uncachedTexts.forEach((text, index) => {
                    if (newTranslations[index]) {
                        allTranslations.set(text, newTranslations[index]);
                    }
                });
            }

            // Apply translations to elements
            applyTranslations(textElements, allTranslations);

            // Update current language
            currentLanguage = targetLanguage;
            saveLanguagePreference(targetLanguage);

            console.log(`Page translated to ${targetLanguage} successfully`);
            showTranslationSuccess();

        } catch (error) {
            console.error('Translation failed:', error);
            showTranslationError(error.message);
            
            // Reset selectors to previous value
            syncLanguageSelectors(currentLanguage);
        } finally {
            isTranslating = false;
            hideTranslationLoading();
        }
    }

    /**
     * Get all elements that should be translated
     */
    function getTranslatableElements() {
        // Elements to translate (excluding navigation, forms, and technical content)
        const selectors = [
            'h1, h2, h3, h4, h5, h6',
            'p',
            '.qld__card__title',
            '.qld__card__description',
            '.qld__banner__title',
            '.qld__banner__description',
            '.qld__callout',
            '.qld__body',
            '[data-translate]'
        ].join(', ');

        // Elements to exclude from translation
        const excludeSelectors = [
            '.qld__header',
            '.qld__main-nav',
            '.qld__footer',
            'form',
            'input',
            'select',
            'button',
            '.qld__breadcrumbs',
            '[data-no-translate]',
            'code',
            'pre'
        ];

        const elements = document.querySelectorAll(selectors);
        
        return Array.from(elements).filter(element => {
            // Exclude if element or any parent matches exclude selectors
            return !excludeSelectors.some(excludeSelector => 
                element.matches(excludeSelector) || element.closest(excludeSelector)
            );
        });
    }

    /**
     * Extract texts for translation from elements
     */
    function extractTextsForTranslation(elements) {
        const texts = [];
        const textMap = new Map();

        elements.forEach((element, index) => {
            const text = element.textContent.trim();
            
            if (text && text.length > 0 && !textMap.has(text)) {
                texts.push(text);
                textMap.set(text, index);
                
                // Store reference for later application
                element.setAttribute('data-translation-ref', texts.length - 1);
            }
        });

        return texts;
    }

    /**
     * Translate texts using Google Translate API
     */
    async function translateTexts(texts, targetLanguage) {
        const translations = [];
        
        // Process in batches to respect API limits
        for (let i = 0; i < texts.length; i += CONFIG.batchSize) {
            const batch = texts.slice(i, i + CONFIG.batchSize);
            const batchTranslations = await translateBatch(batch, targetLanguage);
            translations.push(...batchTranslations);
        }

        return translations;
    }

    /**
     * Translate a batch of texts
     */
    async function translateBatch(texts, targetLanguage, retryCount = 0) {
        try {
            const response = await fetch(
                `https://translation.googleapis.com/language/translate/v2?key=${CONFIG.apiKey}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        q: texts,
                        target: targetLanguage,
                        source: CONFIG.defaultLanguage,
                        format: 'text'
                    })
                }
            );

            if (!response.ok) {
                throw new Error(`Translation API error: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            
            if (data.error) {
                throw new Error(`Translation API error: ${data.error.message}`);
            }

            return data.data.translations.map(t => t.translatedText);

        } catch (error) {
            if (retryCount < CONFIG.maxRetries) {
                console.warn(`Translation retry ${retryCount + 1}/${CONFIG.maxRetries}:`, error);
                await new Promise(resolve => setTimeout(resolve, 1000 * (retryCount + 1)));
                return translateBatch(texts, targetLanguage, retryCount + 1);
            }
            throw error;
        }
    }

    /**
     * Apply translations to elements
     */
    function applyTranslations(elements, translations) {
        elements.forEach(element => {
            const originalText = element.textContent.trim();
            const translation = translations.get(originalText);
            
            if (translation && translation !== originalText) {
                // Store original text for reset
                if (!element.hasAttribute('data-original-text')) {
                    element.setAttribute('data-original-text', originalText);
                }
                
                element.textContent = translation;
                element.setAttribute('data-translated', 'true');
            }
        });
    }

    /**
     * Reset translation to original language
     */
    function resetTranslation() {
        const translatedElements = document.querySelectorAll('[data-translated="true"]');
        
        translatedElements.forEach(element => {
            const originalText = element.getAttribute('data-original-text');
            if (originalText) {
                element.textContent = originalText;
                element.removeAttribute('data-translated');
                element.removeAttribute('data-original-text');
                element.removeAttribute('data-translation-ref');
            }
        });

        currentLanguage = CONFIG.defaultLanguage;
        saveLanguagePreference(CONFIG.defaultLanguage);
        
        // Reset selectors
        const selectors = [
            document.getElementById('language-toggle'),
            document.getElementById('language-toggle-mega')
        ].filter(Boolean);
        
        selectors.forEach(selector => {
            selector.value = '';
        });

        console.log('Translation reset to original language');
    }

    /**
     * Cache management
     */
    function getCachedTranslations(texts, targetLanguage) {
        const cacheKey = `translations_${targetLanguage}`;
        const cached = localStorage.getItem(cacheKey);
        
        if (!cached) return new Map();
        
        try {
            const cacheData = JSON.parse(cached);
            
            // Check if cache is expired
            if (Date.now() - cacheData.timestamp > CONFIG.cacheExpiry) {
                localStorage.removeItem(cacheKey);
                return new Map();
            }
            
            const cachedMap = new Map();
            texts.forEach(text => {
                if (cacheData.translations[text]) {
                    cachedMap.set(text, cacheData.translations[text]);
                }
            });
            
            return cachedMap;
        } catch (error) {
            console.warn('Failed to parse translation cache:', error);
            localStorage.removeItem(cacheKey);
            return new Map();
        }
    }

    function cacheTranslations(texts, translations, targetLanguage) {
        const cacheKey = `translations_${targetLanguage}`;
        let cacheData = { translations: {}, timestamp: Date.now() };
        
        // Load existing cache
        try {
            const existing = localStorage.getItem(cacheKey);
            if (existing) {
                const parsed = JSON.parse(existing);
                if (Date.now() - parsed.timestamp <= CONFIG.cacheExpiry) {
                    cacheData.translations = parsed.translations;
                }
            }
        } catch (error) {
            console.warn('Failed to load existing cache:', error);
        }
        
        // Add new translations
        texts.forEach((text, index) => {
            if (translations[index]) {
                cacheData.translations[text] = translations[index];
            }
        });
        
        cacheData.timestamp = Date.now();
        
        try {
            localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        } catch (error) {
            console.warn('Failed to cache translations:', error);
        }
    }

    /**
     * Language preference management
     */
    function saveLanguagePreference(language) {
        try {
            localStorage.setItem('selectedLanguage', language);
        } catch (error) {
            console.warn('Failed to save language preference:', error);
        }
    }

    function loadSavedLanguage() {
        try {
            const saved = localStorage.getItem('selectedLanguage');
            if (saved && saved !== CONFIG.defaultLanguage) {
                currentLanguage = saved;
                
                // Set selector values when they're available
                const checkSelectors = () => {
                    const selectors = [
                        document.getElementById('language-toggle'),
                        document.getElementById('language-toggle-mega')
                    ].filter(Boolean);
                    
                    if (selectors.length > 0) {
                        selectors.forEach(selector => {
                            selector.value = saved;
                        });
                        
                        if (!CONFIG.useWidget && CONFIG.apiKey) {
                            translatePage(saved);
                        }
                    } else {
                        setTimeout(checkSelectors, 100);
                    }
                };
                checkSelectors();
            }
        } catch (error) {
            console.warn('Failed to load language preference:', error);
        }
    }

    /**
     * UI feedback functions
     */
    function showTranslationLoading() {
        // You can customize this to match your design system
        const selector = document.getElementById('language-toggle');
        if (selector) {
            selector.disabled = true;
            
            // Add loading indicator
            let loadingIndicator = document.getElementById('translation-loading');
            if (!loadingIndicator) {
                loadingIndicator = document.createElement('span');
                loadingIndicator.id = 'translation-loading';
                loadingIndicator.textContent = ' Translating...';
                loadingIndicator.style.fontSize = '0.8em';
                loadingIndicator.style.color = '#666';
                selector.parentNode.appendChild(loadingIndicator);
            }
        }
    }

    function hideTranslationLoading() {
        const selector = document.getElementById('language-toggle');
        if (selector) {
            selector.disabled = false;
        }
        
        const loadingIndicator = document.getElementById('translation-loading');
        if (loadingIndicator) {
            loadingIndicator.remove();
        }
    }

    function showTranslationSuccess() {
        // Optional: Add success feedback
        console.log('Translation completed successfully');
    }

    function showTranslationError(message) {
        console.error('Translation error:', message);
        
        // You can customize this to show user-friendly error messages
        const errorMessage = document.createElement('div');
        errorMessage.id = 'translation-error';
        errorMessage.textContent = `Translation failed: ${message}`;
        errorMessage.style.color = 'red';
        errorMessage.style.fontSize = '0.8em';
        errorMessage.style.marginTop = '5px';
        
        const selector = document.getElementById('language-toggle');
        if (selector && !document.getElementById('translation-error')) {
            selector.parentNode.appendChild(errorMessage);
            
            // Remove error after 5 seconds
            setTimeout(() => {
                errorMessage.remove();
            }, 5000);
        }
    }

    /**
     * Public API for external use
     */
    window.QLDLanguageTranslation = {
        translatePage: translatePage,
        resetTranslation: resetTranslation,
        getCurrentLanguage: () => currentLanguage,
        getSupportedLanguages: () => supportedLanguages,
        setApiKey: (key) => { CONFIG.apiKey = key; },
        setUseWidget: (useWidget) => { CONFIG.useWidget = useWidget; }
    };

})();
